import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import type { AutomationTrigger } from '@hygge/shared';
import { MemberPicker } from './pickers/member-picker';
import { StatusPicker } from './pickers/status-picker';

interface TriggerConfigProps {
  boardId: string;
  trigger: AutomationTrigger;
  onChange: (trigger: AutomationTrigger) => void;
}

// Radix <SelectItem> forbids an empty-string value, so use a sentinel for the optional "any" choice.
const ANY_CATEGORY = '__any__';

const STATUS_CATEGORIES = [
  { value: 'backlog', label: 'Backlog' },
  { value: 'active', label: 'Active' },
  { value: 'done', label: 'Done' },
  { value: 'cancelled', label: 'Cancelled' },
] as const;

const TASK_FIELDS = [
  { value: 'priority', label: 'Priority' },
  { value: 'type', label: 'Type' },
  { value: 'dueDate', label: 'Due Date' },
  { value: 'storyPoints', label: 'Story Points' },
  { value: 'labels', label: 'Labels' },
] as const;

const DUE_OFFSETS = [
  { value: '24h', label: '24 hours before' },
  { value: '1h', label: '1 hour before' },
  { value: 'overdue', label: 'When overdue' },
] as const;

export function TriggerConfig({ boardId, trigger, onChange }: TriggerConfigProps) {
  if (trigger.type === 'task.moved') {
    return (
      <div className="flex flex-col gap-2 mt-2">
        <div>
          <label className="text-xs text-muted-foreground mb-1 block">To status (optional)</label>
          <StatusPicker
            boardId={boardId}
            value={trigger.toStatusId ?? ''}
            onChange={(id) =>
              onChange({
                type: 'task.moved',
                toStatusId: id || undefined,
                toCategory: undefined,
              })
            }
          />
        </div>
        <div>
          <label className="text-xs text-muted-foreground mb-1 block">
            Or to category (optional)
          </label>
          <Select
            value={trigger.toCategory ?? ANY_CATEGORY}
            onValueChange={(val) =>
              onChange({
                type: 'task.moved',
                toStatusId: undefined,
                toCategory:
                  val === ANY_CATEGORY
                    ? undefined
                    : (val as 'backlog' | 'active' | 'done' | 'cancelled'),
              })
            }
          >
            <SelectTrigger className="h-8 text-sm" aria-label="Status category">
              <SelectValue placeholder="Any category" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ANY_CATEGORY}>Any category</SelectItem>
              {STATUS_CATEGORIES.map((c) => (
                <SelectItem key={c.value} value={c.value}>
                  {c.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>
    );
  }

  if (trigger.type === 'task.assigned') {
    return (
      <div className="mt-2">
        <label className="text-xs text-muted-foreground mb-1 block">To member (optional)</label>
        <MemberPicker
          boardId={boardId}
          value={trigger.toAssigneeId}
          onChange={(id) =>
            onChange({
              type: 'task.assigned',
              toAssigneeId: id || undefined,
            })
          }
          ariaLabel="Assignee"
        />
      </div>
    );
  }

  if (trigger.type === 'task.field_changed') {
    return (
      <div className="mt-2">
        <label className="text-xs text-muted-foreground mb-1 block">Field (required)</label>
        <Select
          value={trigger.field}
          onValueChange={(val) =>
            onChange({
              type: 'task.field_changed',
              field: val as 'priority' | 'type' | 'dueDate' | 'storyPoints' | 'labels',
            })
          }
        >
          <SelectTrigger className="h-8 text-sm" aria-label="Field">
            <SelectValue placeholder="Select field" />
          </SelectTrigger>
          <SelectContent>
            {TASK_FIELDS.map((f) => (
              <SelectItem key={f.value} value={f.value}>
                {f.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
    );
  }

  if (trigger.type === 'task.due') {
    return (
      <div className="mt-2">
        <label className="text-xs text-muted-foreground mb-1 block">Timing (required)</label>
        <Select
          value={trigger.offset}
          onValueChange={(val) =>
            onChange({
              type: 'task.due',
              offset: val as '24h' | '1h' | 'overdue',
            })
          }
        >
          <SelectTrigger className="h-8 text-sm" aria-label="Timing">
            <SelectValue placeholder="Select timing" />
          </SelectTrigger>
          <SelectContent>
            {DUE_OFFSETS.map((o) => (
              <SelectItem key={o.value} value={o.value}>
                {o.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
    );
  }

  return null;
}
